import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Image, Table } from "react-bootstrap";
import { useSelector } from "react-redux";
import {
  DeleteModal,
  PageHeader,
  StatusBadge,
  TablePagination,
  TableSearchField,
} from "CommonComponent";
import AccessRights from "utils/AccessRights";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import AddUpdateModal from "./AddUpdateModal";
import {
  createAppraisalType,
  deleteAppraisalType,
  fetchAppraisalTypes,
  updateAppraisalType,
} from "./appraisalTypesApi";

const PafDesignation = () => {
  const session = useSelector((state) => state.session);
  const objectViewedId =
    useObjectViewedId() ||
    session?.current_page_permissions?.object_id ||
    "";

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterValue, setFilterValue] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [modalMode, setModalMode] = useState(null);
  const [activeRecord, setActiveRecord] = useState(null);
  const [serverErrors, setServerErrors] = useState({});
  const [deleteRowId, setDeleteRowId] = useState(null);
  const [deleteModalShow, setDeleteModalShow] = useState(false);
  const [toastError, setToastError] = useState("");

  const loadRows = useCallback(async () => {
    if (!objectViewedId) return;
    setLoading(true);
    setToastError("");
    try {
      const data = await fetchAppraisalTypes(objectViewedId);
      setRows(data);
    } catch (error) {
      setToastError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Unable to load appraisal types."
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [objectViewedId]);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  const filteredData = useMemo(() => {
    const q = filterValue.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const designationNames = (row.designations || [])
        .map((d) => d.name)
        .join(" ");
      return [row.name, row.code, row.description, designationNames]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(q));
    });
  }, [rows, filterValue]);

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [filterValue, rowsPerPage]);

  const openAdd = () => {
    setServerErrors({});
    setActiveRecord(null);
    setModalMode("add");
  };

  const openEdit = (row) => {
    setServerErrors({});
    setActiveRecord(row);
    setModalMode("edit");
  };

  const openView = (row) => {
    setServerErrors({});
    setActiveRecord(row);
    setModalMode("view");
  };

  const closeModal = () => {
    setModalMode(null);
    setActiveRecord(null);
    setServerErrors({});
  };

  const parseServerErrors = (error) => {
    const raw = error?.response?.data?.error || error?.response?.data?.errors;
    if (!raw || typeof raw !== "object") {
      return {
        form: [
          error?.response?.data?.message ||
            "Unable to save appraisal type. Please try again.",
        ],
      };
    }
    const mapped = {};
    Object.keys(raw).forEach((key) => {
      mapped[key] = Array.isArray(raw[key]) ? raw[key] : [String(raw[key])];
    });
    return mapped;
  };

  const handleSubmit = async (payload) => {
    const body = {
      ...payload,
      object_viewed_id: objectViewedId,
    };

    try {
      if (modalMode === "edit" && activeRecord?.id) {
        await updateAppraisalType(activeRecord.id, body);
      } else {
        await createAppraisalType(body);
      }
      closeModal();
      await loadRows();
    } catch (error) {
      setServerErrors(parseServerErrors(error));
      throw error;
    }
  };

  const handleDelete = async () => {
    if (!deleteRowId) return;
    try {
      await deleteAppraisalType(deleteRowId, objectViewedId);
      setDeleteModalShow(false);
      setDeleteRowId(null);
      await loadRows();
    } catch (error) {
      setToastError(
        error?.response?.data?.error ||
          error?.response?.data?.message ||
          "Unable to delete appraisal type."
      );
      setDeleteModalShow(false);
    }
  };

  return (
    <div>
      <PageHeader title="PAF Designation">
        {AccessRights(session.current_page_permissions, "add") ? (
          <button type="button" className="add-new-btn" onClick={openAdd}>
            <Image
              src="/assets/images/add-icon.svg"
              className="me-2 add-user-icon"
              alt=""
              aria-hidden="true"
            />
            Add New
          </button>
        ) : null}
      </PageHeader>

      <div className="main-content-div page-card">
        {toastError ? (
          <p className="text-danger font-13 mb-3">{toastError}</p>
        ) : null}

        <TableSearchField
          value={filterValue}
          onChange={(e) => setFilterValue(e.target.value)}
          className="mb-3"
        />

        <Table responsive>
          <thead>
            <tr>
              <th>Name</th>
              <th>Designations</th>
              <th>Designation Code</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-4">
                  Loading...
                </td>
              </tr>
            ) : currentData.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <div className="empty-state py-4" role="status">
                    <img
                      src="/assets/images/emptyStateTable.png"
                      alt=""
                      className="empty-state__image"
                      aria-hidden="true"
                    />
                    <p className="empty-state__title">No Records Found</p>
                    <p className="empty-state__message">
                      There is nothing to display yet. Try adjusting your
                      search.
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              currentData.map((row) => (
                <tr key={row.id}>
                  <td>{row.name}</td>
                  <td>
                    {(row.designations || []).length === 0
                      ? "—"
                      : row.designations.map((d) => d.name).join(", ")}
                  </td>
                  <td>{row.code || "—"}</td>
                  <td>
                    <StatusBadge
                      label={row.is_active === "y" ? "Active" : "Inactive"}
                      variant={row.is_active === "y" ? "active" : "inactive"}
                    />
                  </td>
                  <td className="form-action-width">
                    <Button
                      variant="none"
                      className="border-0"
                      onClick={() => openView(row)}
                    >
                      <Image src="/assets/images/view-icon.svg" alt="View" />
                    </Button>
                    {AccessRights(session.current_page_permissions, "edit") ? (
                      <Button
                        variant="none"
                        className="border-0"
                        onClick={() => openEdit(row)}
                      >
                        <Image src="/assets/images/edit-icon.svg" alt="Edit" />
                      </Button>
                    ) : null}
                    {AccessRights(
                      session.current_page_permissions,
                      "delete"
                    ) ? (
                      <Button
                        variant="none"
                        className="border-0"
                        onClick={() => {
                          setDeleteRowId(row.id);
                          setDeleteModalShow(true);
                        }}
                      >
                        <Image
                          src="/assets/images/delete-icon.svg"
                          alt="Delete"
                        />
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {filteredData.length > 0 ? (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={rowsPerPage}
            onPageChange={(page) => {
              if (page >= 1 && page <= totalPages) setCurrentPage(page);
            }}
            onPageSizeChange={(value) => {
              setRowsPerPage(Number(value) || 10);
              setCurrentPage(1);
            }}
          />
        ) : null}
      </div>

      <AddUpdateModal
        show={!!modalMode}
        mode={modalMode || "add"}
        record={activeRecord}
        onHide={closeModal}
        onSubmit={handleSubmit}
        serverErrors={serverErrors}
      />

      <DeleteModal
        show={deleteModalShow}
        onDelete={handleDelete}
        onHide={() => {
          setDeleteModalShow(false);
          setDeleteRowId(null);
        }}
      />
    </div>
  );
};

export default PafDesignation;
